import React, { useLayoutEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { BtnV2, l, Tabs } from '@manychat/manyui'

import {
  DisableScenarioConfirm,
  useAutomationStatusToggle,
} from 'apps/aiAutomation/components/DisableScenarioConfirm'
import { EmptyKnowledge } from 'apps/aiAutomation/components/InstantAnswers/components/EmptyKnowledge/EmptyKnowledge'
import { KnowledgeProcessing } from 'apps/aiAutomation/components/InstantAnswers/components/KnowledgeProcessing/KnowledgeProcessing'
import { PreviewTab } from 'apps/aiAutomation/components/InstantAnswers/components/PreviewTab/PreviewTab'
import { SetupTab } from 'apps/aiAutomation/components/InstantAnswers/components/SetupTab/SetupTab'
import { InstantAnswersTab, tabsConfig } from 'apps/aiAutomation/components/InstantAnswers/lib/tabs'
import { SidebarHeader } from 'apps/aiAutomation/components/SidebarHeader/SidebarHeader'
import { useViewState } from 'apps/aiAutomation/hooks/layout'
import { SidebarViewType } from 'apps/aiAutomation/lib/sidebar'
import { AiAlwaysOnAutomationsSelectors } from 'apps/aiAutomation/store/core'
import { LoaderStatus } from 'apps/aiAutomation/store/core/lib/types'
import { AiAutomationKnowledgeSelectors } from 'apps/aiAutomation/store/knowledge'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import { AiAutomationScenario } from 'shared/api/requests/aiAutomation/schemas'
import { useMediaQuery } from 'utils/commonHooks'

import { InvalidKnowledge } from './components/InvalidKnowledge'

import cm from './InstantAnswers.module.css'

export const InstantAnswers = () => {
  const dispatch = useAppDispatch()

  const isFaqScenarioPublished = useAppSelector(AiAlwaysOnAutomationsSelectors.getFaqScenarioStatus)
  const isKnowledgeFilled = Boolean(useAppSelector(AiAutomationKnowledgeSelectors.getList).length)
  const hasProcessedEntries = Boolean(
    useAppSelector(AiAutomationKnowledgeSelectors.getProcessedEntries).length,
  )
  const isAllEntriesInvalid = useAppSelector(AiAutomationKnowledgeSelectors.getIsAllEntriesInvalid)

  const isLoading =
    useAppSelector(AiAlwaysOnAutomationsSelectors.getLoaderStatus) ===
    LoaderStatus.INSTANT_ANSWERS_STATUS_UPDATING

  const { toggleStatus, isConfirmModalOpen, closeConfirmModal } = useAutomationStatusToggle({
    scenario: AiAutomationScenario.INSTANT_ANSWERS,
    source: 'automation_editor',
  })

  const { isOpen } = useViewState(SidebarViewType.INSTANT_ANSWERS)

  const [currentTab, setCurrentTab] = useState<InstantAnswersTab>(InstantAnswersTab.SETUP)

  useLayoutEffect(() => {
    if (hasProcessedEntries && isOpen) {
      dispatch(AiAutomationLayoutActions.expandSidebarChanged(true))
    }
  }, [dispatch, hasProcessedEntries, isOpen])

  const isMobile = useMediaQuery('(max-width: 1024px)')

  if (isAllEntriesInvalid) {
    return <InvalidKnowledge />
  }

  if (!isKnowledgeFilled) {
    return <EmptyKnowledge />
  }

  if (!hasProcessedEntries) {
    return <KnowledgeProcessing />
  }

  return (
    <section className={cm.wrapper}>
      <SidebarHeader
        title={l.translate('Always-On Answers')}
        button={
          <BtnV2
            label={isFaqScenarioPublished ? l.translate('Stop') : l.translate('Go Live')}
            ariaLabel={isFaqScenarioPublished ? l.translate('Stop') : l.translate('Go Live')}
            variant={isFaqScenarioPublished ? 'danger-light' : 'primary'}
            loading={isLoading}
            onClick={toggleStatus}
            data-test-id="ai-automation/instant-answers-status-change-button"
          />
        }
        className={cm.sidebarHeader}
      >
        {isMobile && (
          <Tabs
            className={cm.tabsContainer}
            ariaLabel={l.translate('Tabs')}
            onChange={(tab) => setCurrentTab(tab as InstantAnswersTab)}
            options={tabsConfig}
            value={currentTab}
          />
        )}
      </SidebarHeader>

      <div className={cm.content}>
        {(!isMobile || currentTab === InstantAnswersTab.SETUP) && <SetupTab />}

        <div className={cm.content__delimiter} />

        {(!isMobile || currentTab === InstantAnswersTab.PREVIEW) && <PreviewTab />}
      </div>

      {isConfirmModalOpen && (
        <DisableScenarioConfirm
          onClose={closeConfirmModal}
          scenario={AiAutomationScenario.INSTANT_ANSWERS}
          source="automation_editor"
        />
      )}
    </section>
  )
}
